// Events (/events): Secret Sample nights, live, coming up and past.
//
// One card per event — a calendar tile, the title and blurb, how many are in
// — grouped Live now / Coming up / Past. Admins get the way to make one.

import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Settings2, Shuffle } from "lucide-react";
import type { ApiEventList } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";
import { eventPath } from "@/lib/ctas";
import { AmbientGlow } from "@/components/AmbientGlow";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSession } from "../session";
import { DateBlock, StatusBadge } from "../components/EventParts";

type ListedEvent = ApiEventList["events"][number];

export function useEventList() {
  const session = useSession();
  return useQuery({
    queryKey: ["web", "events", session?.user.id ?? ""],
    queryFn: () => webApi<ApiEventList>("/events"),
  });
}

export function EventsPage() {
  useDocumentTitle("Events");
  const list = useEventList();
  const events = list.data?.events ?? [];
  const live = events.filter((e) => e.status === "assigned");
  // Soonest first for what's coming; newest first for what's done.
  const upcoming = events.filter((e) => e.status === "open").reverse();
  const past = events.filter((e) => e.status === "closed");

  return (
    <div className="mx-auto max-w-3xl space-y-10 pb-10">
      <AmbientGlow />
      <header className="flex flex-wrap items-end justify-between gap-4 pt-2 sm:pt-6">
        <div>
          <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-primary">
            <Shuffle className="size-3.5" /> Secret Sample
          </p>
          <h1 className="mt-1 text-4xl font-black tracking-tighter sm:text-5xl">Events</h1>
        </div>
        {list.data?.isAdmin && (
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/events/admins">
              <Settings2 className="size-4" /> Manage events
            </Link>
          </Button>
        )}
      </header>

      {list.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-28 w-full rounded-2xl" />
        </div>
      ) : list.isError ? (
        <p className="text-sm text-removed">Couldn't load events.</p>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center">
          <p className="text-lg font-black">Nothing scheduled yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">The next Secret Sample gets announced on Discord.</p>
        </div>
      ) : (
        <>
          <Section title="Live now" events={live} featured />
          <Section title="Coming up" events={upcoming} featured={live.length === 0} />
          <Section title="Past" events={past} />
        </>
      )}
    </div>
  );
}

function Section({ title, events, featured = false }: { title: string; events: ListedEvent[]; featured?: boolean }) {
  if (events.length === 0) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{title}</h2>
      <div className="space-y-3">
        {events.map((e, i) => (
          <EventCard key={e.slug} event={e} featured={featured && i === 0} />
        ))}
      </div>
    </section>
  );
}

function EventCard({ event, featured }: { event: ListedEvent; featured: boolean }) {
  const counts = [
    event.participantCount > 0 ? `${event.participantCount} in` : null,
    event.poolCount > 0 ? `${event.poolCount} in the pool` : null,
  ].filter(Boolean);
  return (
    <Link
      to={eventPath(event.slug)}
      className={cn(
        "group flex items-center gap-4 rounded-2xl border p-4 transition-colors sm:gap-5 sm:p-5",
        featured
          ? "border-primary/30 bg-raised shadow-[0_0_60px_-30px_var(--primary)] hover:border-primary/50"
          : "border-white/10 bg-raised/70 hover:border-white/20",
        event.status === "closed" && "opacity-70 hover:opacity-100",
      )}
      style={featured ? { backgroundImage: "linear-gradient(120deg, color-mix(in srgb, var(--primary) 8%, transparent), transparent 60%)" } : undefined}
    >
      <DateBlock iso={event.startsAt} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className={cn("truncate font-black tracking-tight", featured ? "text-2xl" : "text-lg")}>{event.title}</p>
          <StatusBadge status={event.status} />
        </div>
        {event.blurb && <p className="line-clamp-2 whitespace-pre-line text-sm text-muted-foreground">{event.blurb}</p>}
        {counts.length > 0 && <p className="text-xs text-muted-foreground">{counts.join(" · ")}</p>}
      </div>
      <ArrowRight className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
    </Link>
  );
}
